import { editDurationMs, overlayAt, overlaysAt, segmentAt, segmentLengthMs, segmentStarts, strapAnim, strapsAt, textStyleAt, textsAt, type AnimIn, type AnimOut, type EditOverlay, type EditProject, type EditSource, type EditStrap, type EditText, type EditTextStyle } from '@airpost/shared';

/**
 * What an edit shows and sounds like at any second, as plain data: the monitor and the export draw the same
 * frames (`frameAt`, `textDraws`, `overlayDraws`) and schedule the same sound (`mixPlan`), so what the journalist
 * previews is what is written. Transitions are centred on their cut and use the sources' handles (the material just
 * past a piece's out point and just before the next piece's in point); where a source has no handle, its first or
 * last frame holds. A piece plays its source at its speed; a frozen frame and a photo show one picture.
 */

export interface Layer {
  /** The piece drawn. */
  index: number;
  /** The second of its source shown. */
  sourceMs: number;
  /** It stands on one frame here: a frozen frame, a photo, or a handle its source does not have (its first or last frame). */
  held: boolean;
  alpha: number;
  /** The visible part, as fractions of the width (a wipe). */
  clipX0: number;
  clipX1: number;
  /** A shift, as a fraction of the width (a push). */
  dx: number;
  /** How far into the piece, on the sequence (negative or past its length in a transition's handles), and its length: for its move and its effects. */
  localMs: number;
  lengthMs: number;
}
export interface FrameSpec {
  bg: 'black' | 'white';
  layers: Layer[];
}

const clamp = (x: number, a: number, b: number) => Math.min(b, Math.max(a, x));
/** A move across the frame (a push, a wipe) starts and ends gently, as an editing suite's do; a fade stays even. */
const smooth = (q: number) => q * q * (3 - 2 * q);

/** The frame at a second of the sequence: which pieces are drawn, at which second of their source, how. */
export function frameAt(p: EditProject, tMs: number, durationOf: (s: EditSource) => number | null = () => null): FrameSpec {
  const total = editDurationMs(p);
  const t = clamp(tMs, 0, total);
  const hit = segmentAt(p, t);
  if (!hit) return { bg: 'black', layers: [] };
  const starts = segmentStarts(p);
  const srcOf = (k: number): { sourceMs: number; held: boolean } => {
    const s = p.segments[k]!;
    if (s.freeze) return { sourceMs: s.inMs, held: true };
    if (s.source.kind === 'image') return { sourceMs: 0, held: true };
    const dur = durationOf(s.source) ?? Number.POSITIVE_INFINITY;
    // past the out point (an outgoing piece) or before the in point (an incoming one) the source's handles play; where it has none, its first or last frame holds
    const at = s.inMs + (t - starts[k]!) * s.speed;
    return { sourceMs: clamp(at, 0, dur), held: at < 0 || at > dur };
  };
  const layer = (index: number, extra: Partial<Layer> = {}): Layer => ({ index, ...srcOf(index), alpha: 1, clipX0: 0, clipX1: 1, dx: 0, localMs: t - starts[index]!, lengthMs: segmentLengthMs(p.segments[index]!), ...extra });
  const i = hit.index;
  let spec: FrameSpec = { bg: 'black', layers: [layer(i)] };
  const own = p.segments[i]!.transitionIn;
  const next = p.segments[i + 1]?.transitionIn ?? null;
  if (i === 0 && own && t < own.durationMs) {
    // the first piece comes out of black (or white)
    spec = { bg: own.type === 'dip-white' ? 'white' : 'black', layers: [layer(0, { alpha: t / own.durationMs })] };
  } else {
    let b = -1;
    if (i > 0 && own && t < starts[i]! + own.durationMs / 2) b = i;
    else if (next && t >= starts[i + 1]! - next.durationMs / 2) b = i + 1;
    if (b > 0) {
      const tr = p.segments[b]!.transitionIn!;
      const q = clamp((t - (starts[b]! - tr.durationMs / 2)) / tr.durationMs, 0, 1);
      const a = b - 1;
      switch (tr.type) {
        case 'dissolve':
          spec = { bg: 'black', layers: [layer(a), layer(b, { alpha: q })] };
          break;
        case 'dip-black':
        case 'dip-white':
          spec = { bg: tr.type === 'dip-white' ? 'white' : 'black', layers: q < 0.5 ? [layer(a, { alpha: 1 - 2 * q })] : [layer(b, { alpha: 2 * q - 1 })] };
          break;
        case 'wipe':
          spec = { bg: 'black', layers: [layer(a), layer(b, { clipX1: smooth(q) })] };
          break;
        case 'push':
          spec = { bg: 'black', layers: [layer(a, { dx: -smooth(q) }), layer(b, { dx: 1 - smooth(q) })] };
          break;
      }
    }
  }
  if (p.endFade && t > total - p.endFade.durationMs) {
    const q = clamp((t - (total - p.endFade.durationMs)) / p.endFade.durationMs, 0, 1);
    spec = { bg: p.endFade.color, layers: spec.layers.map((l) => ({ ...l, alpha: l.alpha * (1 - q) })) };
  }
  return spec;
}

/**
 * What the monitor does with a layer's video element: play it from a second of its source, or stand on one. A frame
 * that holds stands, and so does the file's last moment (an element told to play at its end starts over), so a
 * transition into a piece's missing handle does not seek on every tick. A piece about to appear (`aheadMs` early)
 * rolls from where it is now, or waits on its first frame until it gets there.
 */
export function deckMove(l: Layer, speed: number, fileEndS: number, rolling: boolean, aheadMs = 0): { play: boolean; atS: number } {
  const atS = Math.min((l.sourceMs - (l.held ? 0 : aheadMs * speed)) / 1000, fileEndS);
  return { play: rolling && !l.held && atS >= 0 && atS < fileEndS - 0.05, atS: Math.max(0, atS) };
}

// ── texts and pictures over the sequence ──

/** An item's state in its animation: opacity, shift (fractions of the frame), size, and how much of a text is typed. */
export interface Anim {
  alpha: number;
  dx: number;
  dy: number;
  scale: number;
  /** 0..1 of the characters shown (the typewriter). */
  reveal: number;
  /** 0..1 of the item's width shown from its left (the wipe). */
  wipe: number;
}
const easeOut = (q: number) => 1 - (1 - q) ** 3;
/** A little past full size and back: the pop. */
const easeBack = (q: number) => 1 + 2.2 * (q - 1) ** 3 + 1.2 * (q - 1) ** 2;

/** How an item animates `localMs` into its `durationMs`: in over `animMs` (at most half of it), out over the same at its end. */
export function animAt(animIn: AnimIn, animOut: AnimOut, animMs: number, localMs: number, durationMs: number): Anim {
  const a: Anim = { alpha: 1, dx: 0, dy: 0, scale: 1, reveal: 1, wipe: 1 };
  const d = Math.min(animMs, durationMs / 2);
  if (animIn === 'typewriter') {
    // the words type over most of the item, at least the animation's length
    const typing = Math.min(durationMs * 0.8, Math.max(animMs, 1500));
    a.reveal = typing > 0 ? clamp(localMs / typing, 0, 1) : 1;
  } else if (d > 0 && localMs < d) {
    const q = easeOut(clamp(localMs / d, 0, 1));
    if (animIn === 'fade') a.alpha *= q;
    else if (animIn === 'slide-up') {
      a.alpha *= q;
      a.dy += (1 - q) * 0.08;
    } else if (animIn === 'slide-left') {
      a.alpha *= q;
      a.dx += (1 - q) * 0.12;
    } else if (animIn === 'pop') {
      a.alpha *= clamp(q * 2, 0, 1);
      a.scale *= 0.5 + 0.5 * easeBack(clamp(localMs / d, 0, 1));
    } else if (animIn === 'wipe') a.wipe = q;
  }
  const left = durationMs - localMs;
  if (d > 0 && left < d) {
    const q = easeOut(clamp(left / d, 0, 1));
    if (animOut === 'fade') a.alpha *= q;
    else if (animOut === 'slide-down') {
      a.alpha *= q;
      a.dy += (1 - q) * 0.08;
    } else if (animOut === 'slide-right') {
      a.alpha *= q;
      a.dx += (1 - q) * 0.12;
    } else if (animOut === 'pop') {
      a.alpha *= q;
      a.scale *= 0.6 + 0.4 * q;
    } else if (animOut === 'wipe') a.wipe = Math.min(a.wipe, q);
  }
  return a;
}

export interface TextDraw {
  text: EditText;
  /** Its style at that moment, its keyframes applied. */
  style: EditTextStyle;
  anim: Anim;
}
export interface OverlayDraw {
  /** The picture at that moment, its keyframes applied. */
  overlay: EditOverlay;
  anim: Anim;
}
/** The texts on screen at a second, each at its keyframes and in its animation. */
export const textDraws = (p: Pick<EditProject, 'texts'>, tMs: number): TextDraw[] => textsAt(p, tMs).map((text) => ({ text, style: textStyleAt(text, tMs - text.atMs), anim: animAt(text.animIn, text.animOut, text.animMs, tMs - text.atMs, text.durationMs) }));
/** The pictures over the sequence at a second, each at its keyframes and in its animation (a typewriter in is a fade for a picture). */
export const overlayDraws = (p: Pick<EditProject, 'overlays'>, tMs: number): OverlayDraw[] =>
  overlaysAt(p, tMs).map((o) => ({ overlay: overlayAt(o, tMs - o.atMs), anim: animAt(o.animIn === 'typewriter' ? 'fade' : o.animIn, o.animOut, o.animMs, tMs - o.atMs, o.durationMs) }));

export interface StrapDraw {
  strap: EditStrap;
  anim: Anim;
}
/** The straps on screen at a second, each in its animation (ADR 089). */
export const strapDraws = (p: Pick<EditProject, 'straps'>, tMs: number): StrapDraw[] =>
  strapsAt(p, tMs).map((strap) => {
    const a = strapAnim(strap);
    return { strap, anim: animAt(a.animIn, a.animOut, a.animMs, tMs - strap.atMs, strap.durationMs) };
  });

// the sound is planned in the shared package (the playout render uses it too)
export { gainAt, lowSections, mixPlan, musicEnvelope, musicFractionAt, type GainPoint, type MixPlan, type Voice } from '@airpost/shared';
